import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, MailPlus, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../api";

function ReportRecipients() {
  const [emails, setEmails] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/connect/settings/report-recipients").then((r) => setEmails(r.data.emails)).catch(() => setEmails(null));
  useEffect(() => { load(); }, []);
  if (emails === null) return null;

  const save = async (list) => {
    setBusy(true);
    try {
      const r = await api.put("/connect/settings/report-recipients", { emails: list });
      setEmails(r.data.emails);
      toast.success("Report recipients updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update recipients");
    }
    setBusy(false);
  };

  const addEmail = (e) => {
    e.preventDefault();
    const em = newEmail.trim().toLowerCase();
    if (!em || !em.includes("@")) { toast.error("Enter a valid email address"); return; }
    if (emails.includes(em)) { toast.error("Already in the list"); return; }
    save([...emails, em]);
    setNewEmail("");
  };

  return (
    <div data-testid="report-recipients" className="rounded-2xl bg-white border border-pine/8 shadow-soft p-7 space-y-4">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-slate-400">
        <Mail className="h-4 w-4 text-gold" /> Daily Report Recipients
      </p>
      <p className="text-xs text-slate-500">The Daily Management Report is emailed to these addresses every day at 9:00 PM IST.</p>
      <div className="space-y-2">
        {emails.map((em) => (
          <div key={em} data-testid={`recipient-${em}`} className="flex items-center justify-between rounded-xl border border-pine/10 px-4 py-2.5">
            <span className="text-sm font-semibold text-pine-deep">{em}</span>
            <button type="button" data-testid={`recipient-remove-${em}`} onClick={() => save(emails.filter((x) => x !== em))} disabled={busy || emails.length === 1}
              className="text-red-500 hover:text-red-600 disabled:opacity-40" aria-label={`Remove ${em}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <form onSubmit={addEmail} className="flex gap-2">
        <input type="email" required data-testid="recipient-new" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
          placeholder="Add recipient email" className="h-11 flex-1 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
        <button disabled={busy} data-testid="recipient-add" className="inline-flex items-center gap-1.5 rounded-full bg-pine px-4 py-2 text-xs font-bold text-ivory hover:bg-pine-deep disabled:opacity-50">
          <MailPlus className="h-3.5 w-3.5" /> Add
        </button>
      </form>
    </div>
  );
}

export default function Settings() {
  const nav = useNavigate();
  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const [form, setForm] = useState({ name: me.name || "", email: me.email || "", current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/connect/account", {
        name: form.name,
        email: form.email,
        current_password: form.current_password,
        new_password: form.new_password || undefined,
      });
      if (data.relogin) {
        toast.success("Login details updated. Please sign in again.");
        localStorage.removeItem("dyntra_connect_token");
        localStorage.removeItem("dyntra_connect_user");
        setTimeout(() => nav("/connect", { replace: true }), 1200);
      } else {
        toast.success("Profile updated.");
        localStorage.setItem("dyntra_connect_user", JSON.stringify({ ...me, name: form.name }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update account");
    } finally {
      setBusy(false);
    }
  };

  const field = "h-12 w-full rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine";

  return (
    <div data-testid="connect-settings" className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-display text-2xl font-semibold text-pine-deep">Settings</h2>
        <p className="text-sm text-slate-500">Manage your Dyntra Connect login details.</p>
      </div>

      <form onSubmit={submit} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-7 space-y-4">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-slate-400">
          <KeyRound className="h-4 w-4 text-gold" /> Account &amp; Login
        </p>
        <div>
          <label className="text-xs font-bold text-pine/60">Display Name</label>
          <input data-testid="settings-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-1.5 ${field}`} />
        </div>
        <div>
          <label className="text-xs font-bold text-pine/60">Login Email</label>
          <input data-testid="settings-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`mt-1.5 ${field}`} />
        </div>
        <div>
          <label className="text-xs font-bold text-pine/60">New Password <span className="font-normal text-slate-400">(leave blank to keep current)</span></label>
          <input data-testid="settings-new-password" type="password" minLength={8} placeholder="Minimum 8 characters" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className={`mt-1.5 ${field}`} />
        </div>
        <div>
          <label className="text-xs font-bold text-pine/60">Current Password <span className="text-red-500">*</span></label>
          <input required data-testid="settings-current-password" type="password" placeholder="Required to confirm changes" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className={`mt-1.5 ${field}`} />
        </div>
        <button disabled={busy} data-testid="settings-save" className="inline-flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-bold text-ivory shadow-lift hover:bg-pine-deep transition-all disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save Changes
        </button>
        <p className="text-xs text-slate-400">Changing your email or password will sign you out so you can log back in with the new details.</p>
      </form>

      {["super_admin", "owner", "admin"].includes(me.level || "") && <ReportRecipients />}
    </div>
  );
}
